import React, {useState, Fragment, useCallback, useEffect} from 'react';
import {translate} from '../Services/translation';
import {useDispatch, useSelector} from 'react-redux';
import {reloadPayment} from '../Actions/Checkout.action';
import {update, loadError} from '../Actions/Cart.action';
import * as debounce from 'lodash.debounce';
import {catchError} from '../Actions/Error.action';

const mapCartToAbort = {};

const quantityDispatch = debounce((articleNumber, quantity, dispatch) => {
    mapCartToAbort[articleNumber] = new AbortController();
    dispatch(update(articleNumber, quantity, mapCartToAbort[articleNumber]))
        .then(() => dispatch(reloadPayment()))
        .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
}, 500);

const CartItemRow = ({order}) => {
    const dispatch = useDispatch();
    const [removingRow, setRemovingRow] = useState({});
    const cart = useSelector((state) => state.cart);
    const [orderRows, setOrderRows] = useState(cart?.orderRows);

    const { payload } = useSelector((state) => state.checkout);
    const { authenticated } = payload;

    useEffect(() => {
        setOrderRows(cart?.orderRows);
    }, [cart]);

    const removeOrderRow = useCallback(
        (rowSystemId) =>
            dispatch(update(rowSystemId, 0)).then(() =>
                dispatch(reloadPayment())
            ),
        [dispatch]
    );

    const handleQuantityInput = useCallback(
        (rowSystemId, quantity, ensureCorrectness = true) => {
            // In case of onBlur event, we need ensureCorrectness as true in order to ensure if the value is a valid float number
            // In case of onChange event, we accept invalid float number, but we don't send any request to the server.
            let floatQuantity = parseFloat(quantity);
            let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
            if (ensureCorrectness) {
                quantity = validValue ? floatQuantity : 1;
                floatQuantity = quantity;
                validValue = true;
            }
            const index = orderRows.findIndex(
                (item) => item.rowSystemId === rowSystemId
            );
            const oldQuantity = parseFloat(orderRows[index].quantity);
            if (floatQuantity !== oldQuantity) {
                mapCartToAbort[rowSystemId] &&
                mapCartToAbort[rowSystemId].abort();
                quantityDispatch && quantityDispatch.cancel();
                validValue &&
                quantityDispatch(rowSystemId, quantity, dispatch);
            }
            const tempOrderRows = [...orderRows];
            tempOrderRows[index] = {
                ...tempOrderRows[index],
                quantity,
            };
            setOrderRows(tempOrderRows);
        },
        [dispatch, orderRows]
    );

    const ProductImage = useCallback(({order}) => {
        return (
            <img
                className="cart__item-image"
                src={order.image}
                alt={order.name}
            />
        );
    }, []);

    const ProductName = useCallback(({order}) => {
        return (
            <Fragment>
                <a className="cart__item-title" href={order.url}>{order.name}</a>
                {order.placement &&
                    <span
                        className="checkout-cart__placement">{translate('checkout.cart.placement')} {order.placement}</span>
                }
                {order.pyramidArticleNumber && <p>
                    <strong>{translate('checkout.articlenumber')}</strong>: {order.pyramidArticleNumber}
                </p>}
            </Fragment>
        );
    }, []);

    const ProductPrice = useCallback(({order}) => {
        return (
            <p className="cart__item-price">
                {order.isFreeGift ? order.campaignPrice : order.price}
            </p>
        );
    }, []);

    const ProductQuantity = useCallback(
        (order) => {
            return (
                <Fragment>
                    {order.isFreeGift || order.isMandatoryAccessory ? (
                        <div>{order.quantity}</div>
                    ) : (
                        <input
                            className="checkout-cart__input"
                            type="number"
                            min="1"
                            maxLength={3}
                            value={order.quantity.toString()}
                            onChange={(event) =>
                                handleQuantityInput(
                                    order.rowSystemId,
                                    event.target.value,
                                    false
                                )
                            }
                            onBlur={(event) =>
                                handleQuantityInput(
                                    order.rowSystemId,
                                    event.target.value,
                                )
                            }
                        />
                    )}
                </Fragment>
            );
        },
        [handleQuantityInput]
    );

    const ProductTotalPrice = ({order}) => {
        return (
            <p className="cart__item-price cart__item-price--total">
                {order.isFreeGift ? order.totalCampaignPrice : order.totalPrice}
            </p>
        );
    };

    const RemoveBtn = useCallback(
        ({order}) => {
            return (
                <div>
                    {!order.isFreeGift && !order.isMandatoryAccessory && !removingRow[order.rowSystemId] && (
                        <a
                            className="cart__item-action-button cart__item-action-button--delete"
                            onClick={() =>
                                setRemovingRow({
                                    ...removingRow,
                                    [order.rowSystemId]: true,
                                })
                            }
                            title={translate('general.remove')}
                        ></a>
                    )}
                    {!order.isFreeGift && !order.isMandatoryAccessory && removingRow[order.rowSystemId] && (
                        <Fragment>
                            <a
                                className="cart__item-action-button cart__item-action-button--accept"
                                onClick={() =>
                                    removeOrderRow(order.rowSystemId)
                                }
                                title={translate('general.ok')}
                            ></a>
                            <a
                                className="cart__item-action-button cart__item-action-button--cancel"
                                onClick={() =>
                                    setRemovingRow({
                                        ...removingRow,
                                        [order.rowSystemId]: false,
                                    })
                                }
                                title={translate('general.cancel')}
                            ></a>
                        </Fragment>
                    )}
                </div>
            );
        },
        [removeOrderRow, removingRow]
    );

    const [showDetails, setShowDetails] = useState(false);

    return (
        <div className="cart__item"
             key={order.rowSystemId}>
            <div className="cart__item-body">
                <div className="small-12 checkout-cart__image-container">
                    <div className="cart__item-wrapper">
                        <div className="cart__item-row">
                            <div className="cart__item-column cart__item-column--info-container">
                                <div className="flex items-center">
                                    <div className="cart__item-image-wrapper">
                                        <ProductImage order={order}/>
                                    </div>
                                    <div>
                                        <p className="cart__item-type">{order.type}</p>
                                        <ProductName order={order}/>
                                    </div>
                                </div>
                            </div>
                            <div className="cart__item-column cart__item-column--price">
                                {authenticated && <ProductPrice order={order}/>}
                            </div>
                            <div className="cart__item-column cart__item-column--quantity">
                                {ProductQuantity(order)}
                                <RemoveBtn order={order}/>
                            </div>
                            <div className="cart__item-column cart__item-column--price">
                                {authenticated && <ProductTotalPrice order={order}/>}
                            </div>
                        </div>
                        <div className="cart__item-footer">
                            <div>
                                {showDetails && <ul className="cart__item-accessories-list">
                                    {order.accessories?.map(accessory => {
                                        const accessoryName = `${accessory.name} ${accessory.placement && ("(" + translate('checkout.placement', accessory.placement) + ")")}`;
                                        const priceTag = accessory.totalPriceIncludingVat <= 0
                                            ? translate('checkout.articleincluded')
                                            : accessory.price;
                                        
                                        return <li key={`cart-item-${order.rowSystemId}`} className="cart__item-accessories-list-item">
                                            <img className="cart__item-accessory-image" src={accessory.image} alt=""/>
                                            <div className="cart__item-accessory-type">
                                                <div>
                                                    <span
                                                        className="cart__item-accessory-type--bold">{accessory.type}</span>: {accessoryName}
                                                </div>
                                                <div>
                                                    <span
                                                        className="cart__item-accessory-type--bold">{translate('checkout.articlenumber')}</span>: {accessory.pyramidArticleNumber}
                                                </div>
                                                {authenticated && (
                                                    <div>
                                                        <span className="cart__item-accessory-type--bold">{translate('checkout.cart.header.price')}</span>: {priceTag}
                                                    </div>
                                                )}
                                            </div>
                                        </li>;
                                    })}
                                </ul>}

                                {order.accessories?.length > 0 &&
                                    <button onClick={() => setShowDetails(!showDetails)} className="cart__item-details">
                                    {showDetails ? translate('cart.hideconfigurationdetails') : translate('cart.viewconfigurationdetails')}
                                </button>}
                                
                            </div>
                        </div>
                    </div>

                    <div className="cart__item-wrapper cart__item-wrapper--mobile">
                        <div className="cart__item-row">
                            <div>
                                <div className="w-full flex items-center justify-between">
                                    <div className="w-full flex items-center">
                                        <div className="cart__item-image-wrapper">
                                            <ProductImage order={order}/>
                                        </div>
                                        <div>
                                            <p className="cart__item-type">{order.type}</p>
                                            <ProductName order={order}/>
                                        </div>
                                    </div>
                                    <div className="cart__item-mobile-price-container">
                                        {authenticated && <ProductTotalPrice order={order}/>}
                                    </div>
                                </div>
                            </div>
                            <div className="cart__item-footer">
                                <div>
                                    {showDetails && <ul className="cart__item-accessories-list">
                                        {order.accessories?.map(accessory => {
                                            const accessoryName = `${accessory.name} ${accessory.placement && ("(" + translate('checkout.placement', accessory.placement) + ")")}`;
                                            const priceTag = accessory.totalPriceIncludingVat <= 0
                                                ? translate('checkout.articleincluded')
                                                : accessory.price;
            
                                            return <li key={`cart-item-mobile-${order.rowSystemId}`}
                                                       className="cart__item-accessories-list-item">
                                                <img className="cart__item-accessory-image"
                                                     src={accessory.image} alt=""/>
                                                <div className="cart__item-accessory-type">
                                                    <div>
                                                        <span className="cart__item-accessory-type--bold">{accessory.type}</span>: {accessoryName}
                                                    </div>
                                                    <div>
                                                        <span className="cart__item-accessory-type--bold">{translate('checkout.articlenumber')}</span>: {accessory.pyramidArticleNumber}
                                                    </div>
                                                    {authenticated && (
                                                        <div>
                                                            <span className="cart__item-accessory-type--bold">{translate('checkout.cart.header.price')}</span>: {priceTag}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>;
                                        })}
                                    </ul>}

                                    <button onClick={() => setShowDetails(!showDetails)}
                                        className="cart__item-details">{showDetails ? translate('cart.hideconfigurationdetails') : translate('cart.viewconfigurationdetails')}</button>
                                </div>
                            </div>
                            <div className="cart__item-mobile-actions-container">
                                {ProductQuantity(order)}
                                <RemoveBtn order={order}/>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartItemRow;
